import { ComponentProps, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { Breakpoints, theme } from '@hhgtech/hhg-components/misc'
import { Box } from '@mantine/core'
import innertext from 'innertext'
import { useIntl } from 'react-intl'

import { AdBlock } from 'components/molecules/adBlock'
import { StyledAdHeader } from 'components/molecules/adBlock/index.styled'
import { getMetaTag } from 'components/templates/article/utils'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { Article } from 'interfaces/types'
import {
  getAdTag,
  getCategorySlugFromArticleSlug,
  getCountryAdTag,
  getLocaleCategoryAdTag,
} from 'utils/ads'
import { fastdom } from 'utils/fastdom'

export const MastheadAd = ({
  adUnitPath,
  customTargetting,
  isSponsored,
  defaultNoScroll,
  initialSticky = false,
  className,
  hasPlaceholder,
  onCloseAd,
  containerClassName,
}: {
  adUnitPath: string
  customTargetting?: ComponentProps<typeof AdBlock>['customTargetings']
  isSponsored?: boolean
  defaultNoScroll?: boolean
  initialSticky?: boolean
  className?: string
  hasPlaceholder?: boolean
  onCloseAd?: () => void
  containerClassName?: string
}) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const [showPadding, setShowPadding] = useState(!!hasPlaceholder)
  const [sticky, setSticky] = useState(initialSticky)

  useEffect(() => {
    let timeoutValue: ReturnType<typeof setTimeout> | null = null
    const checkShowPadding = () => {
      fastdom.measure(() => {
        const divAd = document.querySelector('[id^=super-masthead-ad-]')
        if ((divAd?.getBoundingClientRect().height || 0) > 10) {
          setShowPadding(true)
        }
      })
      timeoutValue = setTimeout(checkShowPadding, 1000)
    }
    timeoutValue = setTimeout(checkShowPadding, 1000)

    window.googletag = window.googletag || { cmd: [] }
    const { googletag } = window
    googletag.cmd.push(() => {
      googletag.pubads().addEventListener('slotRenderEnded', (e: any) => {
        if (e.slot?.getSlotElementId().startsWith('super-masthead-ad')) {
          setTimeout(() => {
            setSticky(false)
          }, 4000)
          setTimeout(() => {
            document.querySelector('html')?.classList.remove('ad-noscroll')
          }, 700)
        }
      })
    })

    // backup in case get adBlock, ad load failed
    setTimeout(() => {
      document.querySelector('html')?.classList.remove('ad-noscroll')
    }, 1500)

    return () => {
      if (timeoutValue) {
        clearTimeout(timeoutValue)
      }
    }
  }, [])

  return (
    <Box
      className={containerClassName}
      sx={(theme) => ({
        ...(hasPlaceholder && {
          position: 'relative',
          height: 234,
          backgroundColor: '#f8f9fc',
          '.article-masthead': {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
          [theme.fn.largerThan('sm')]: {
            height: 296,
          },
        }),
        [theme.fn.smallerThan('sm')]: {
          ...(sticky && {
            position: 'sticky',
            top: 0,
            zIndex: 304,
            width: '100%',
            overflow: 'hidden',
          }),
        },
      })}
    >
      <>
        <MastheadAdContainer
          data-visible={showPadding}
          data-sponsored={isSponsored}
          data-sticky={sticky}
          className={className}
        >
          {defaultNoScroll && (
            <script
              async
              dangerouslySetInnerHTML={{
                __html: `
                if (window.innerWidth > ${Breakpoints.BREAK_POINT_MOBILE}) {
                  document.querySelector('html')?.classList.add('ad-noscroll')
                }
              `,
              }}
            ></script>
          )}
          {showPadding && (
            <StyledAdHeader>
              <img src="/svg/adsIcon.svg" alt="ads sign icon" loading="lazy" />
              <span>
                {f({ id: 'adBanner.title', defaultMessage: 'Advertisement' })}
              </span>
            </StyledAdHeader>
          )}
          <AdBlock
            adUnitPath={adUnitPath}
            size={
              isMobile
                ? [
                    [414, 200],
                    [375, 188],
                    [1, 1],
                  ]
                : [
                    [970, 250],
                    [1, 1],
                  ]
            }
            divId={`super-masthead-ad-${isMobile ? 'mobile' : 'desktop'}-${
              customTargetting?.Content_ID
            }`}
            pos={['masthead']}
            key={String(isMobile)}
            customTargetings={customTargetting}
            contentId={String(customTargetting?.Content_ID)}
            adDefaultSize={[0, 0]}
            noWrapper
            showCloseBtn
            wrapperProps={{ m: 0 }}
            onCloseAd={onCloseAd}
          />
        </MastheadAdContainer>
      </>
    </Box>
  )
}

export const ArticleMastheadAd = ({
  article,
  defaultNoScroll,
  className,
  initialSticky,
  hasPlaceholder: hasPlaceholderProp,
}: {
  article: Article
  defaultNoScroll?: boolean
  initialSticky?: boolean
  className?: string
  hasPlaceholder?: boolean
}) => {
  const { locale } = useRouterExtend()
  const isMobile = useIsMobile()
  const [hasPlaceholder, setPlaceholder] = useState(Boolean(hasPlaceholderProp))
  if (!article) return null
  const contentAdTag = getLocaleCategoryAdTag(
    locale,
    getCategorySlugFromArticleSlug(article.permalink),
  )
  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag,
  })

  const customTargetting = {
    Is_Sponsored: [article.isSponsored ? 1 : 0],
    Is_Expert_Article: [
      article.expert?.id ||
      (article.articleReviewed?.expert?.ID &&
        article.articleReviewed?.is_author)
        ? 1
        : 0,
    ],
    Is_Affiliate: [article.hasAffiliate ? 1 : 0],
    Keywords: [
      innertext(getMetaTag(article.metaTag || '', 'content_tags') || ''),
    ],
    Content_ID: String(article.id),
    Brand: [innertext(article?.sponsor?.title || '')],
  }
  return (
    <MastheadAd
      adUnitPath={adUnitPath}
      customTargetting={customTargetting}
      isSponsored={!!article.isSponsored}
      defaultNoScroll={defaultNoScroll}
      initialSticky={initialSticky}
      className={className}
      hasPlaceholder={hasPlaceholder}
      onCloseAd={() => setPlaceholder(false)}
    />
  )
}

export const MastheadAdContainer = styled.div`
  background: ${theme.colors.neutral50};
  &[data-visible='true'] {
    padding: 1rem 0;
    &[data-sponsored='true'] {
      padding: 0 0 1rem;
    }
  }
`
